import { computed, ref } from 'vue'

import {
  useBooksyEppo,
  useLogger,
} from '@booksy-com/vue-tools'

import pkg from '@/../package.json'

import {
  EppoFeatureFlags,
  EppoExperiments,
  APP_DOMAIN,
} from './consts'

import { until } from '@vueuse/core'
import { useToast } from '@/composables/toast'

import type { BooksyEppoInstance } from './types'
import { createEppoFallbackInstance } from './eppo-fallback'

type EppoInstanceWithInternal = BooksyEppoInstance & { __ff: BooksyEppoInstance['ff'] }

declare global {
  interface Window {
    eppoMock?: Record<string, unknown>
  }
}

let INSTANCE: EppoInstanceWithInternal | null = null

const isReady = ref(false)
const logger = useLogger('use-eppo-composable')

export const whenEppoIsReady = until(isReady).toBe(true)

/** Only defined in test mocks; undefined in production. */
export const MOCKED_ASSIGNMENT: {
  experiment: string
  subject: string
  subjectAttributes: { subject_type: string; app_domain: string }
  variation: string
  timestamp: string
} | undefined = undefined

export * from './consts'

type StoreState = {
  state: {
    app: { country: string; fingerprint: string; mode: string }
    business?: { business?: { id?: string; primary_category_internal_name?: string } }
  }
}

export async function createEppo (store: StoreState): Promise<BooksyEppoInstance | undefined> {
  if (INSTANCE) {
    throw new Error('EppoInstance already created')
  }

  try {
    const rawInstance = await useBooksyEppo({
      app: (pkg as { name: string }).name,
      app_version: (pkg as { version: string }).version,
      app_domain: APP_DOMAIN,
      origin: process.env.VUE_APP_PROXY_URL
        || process.env.VUE_APP_API_URL
        || window.location.origin,
      country: computed(() => store.state.app.country),
      flags: {
        ...EppoFeatureFlags,
        ...EppoExperiments,
      } as Parameters<typeof useBooksyEppo>[0]['flags'],
      providers: {
        fingerprint: computed(() => store.state.app.fingerprint),
        business_id: computed(() => store.state.business?.business?.id),
      },
      clientConfig: {
        pollAfterSuccessfulInitialization: true,
      },
      commonAttributes: {
        business_primary_category: computed(() => store.state.business?.business?.primary_category_internal_name),
      },
    })

    const eppoInstance = rawInstance as unknown as EppoInstanceWithInternal
    INSTANCE = eppoInstance
    eppoInstance.__ff = eppoInstance.ff

    eppoInstance.ff = (flagName: string, ...rest: unknown[]): boolean | string => {
      try {
        return (window.eppoMock && flagName in window.eppoMock)
          ? (window.eppoMock[flagName] as boolean | string)
          : eppoInstance.__ff(flagName, ...rest)
      } catch (e) {
        logger.error(e)
        return false
      }
    }

    eppoInstance.experiment = (flagName: string): string | boolean => {
      const flagValue = eppoInstance.ff(flagName)
      if (store.state.app.mode === 'development') {
        const toast = useToast()
        toast.show(`Experiment \`${flagName}\` checked. Returned value: ${flagValue}.`, {
          duration: 5000,
        })
      }
      return flagValue as string | boolean
    }

    return INSTANCE
  } catch (e) {
    logger.error(e)
    return undefined
  } finally {
    isReady.value = true
  }
}

export function useEppo (): BooksyEppoInstance {
  if (!isReady.value || !INSTANCE) {
    logger.warn('BooksyEppo instance not created yet; return fallback with defaults value')
    return createEppoFallbackInstance()
  }

  return INSTANCE
}
